import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Paper, Typography, Button } from '@mui/material';
import { FileDownload, Print, Close } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useExport } from '@hooks/useExport';
import { Snackbar } from '@components/common/Snackbar';

interface SessionActionsProps {
  selectedCount: number;
  selectedSessionIds: string[];
  onClearSelection: () => void;
}

export function SessionActions({
  selectedCount,
  selectedSessionIds,
  onClearSelection,
}: SessionActionsProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { exportCSV, loading: exportLoading } = useExport();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleExport = async () => {
    try {
      await exportCSV(selectedSessionIds);
      setSuccess(t('sessionActions.exportSuccess'));
    } catch {
      setError(t('sessionActions.exportError'));
    }
  };

  const handlePrintSummaries = () => {
    navigate(`/print-summaries?ids=${selectedSessionIds.join(',')}`);
  };

  return (
    <>
      <Paper
        sx={{
          position: 'sticky',
          bottom: 72,
          left: 0,
          right: 0,
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
          zIndex: (theme) => theme.zIndex.appBar - 1,
          borderTop: 1,
          borderColor: 'divider',
        }}
        elevation={4}
        className="no-print"
      >
        <Typography variant="body2" fontWeight={600} sx={{ mr: 'auto' }}>
          {t('sessionActions.selectedCount', { count: selectedCount })}
        </Typography>
        <Button
          size="small"
          variant="contained"
          startIcon={<FileDownload />}
          onClick={handleExport}
          disabled={exportLoading}
        >
          {t('sessionActions.exportCSV')}
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<Print />}
          onClick={handlePrintSummaries}
        >
          {t('sessionActions.print')}
        </Button>
        <Button
          size="small"
          startIcon={<Close />}
          onClick={onClearSelection}
        >
          {t('sessionActions.clearSelection')}
        </Button>
      </Paper>

      <Snackbar
        open={!!error}
        message={error || ''}
        severity="error"
        onClose={() => setError(null)}
      />
      <Snackbar
        open={!!success}
        message={success || ''}
        severity="success"
        onClose={() => setSuccess(null)}
      />
    </>
  );
}
